import { PaginatedResponse } from "@elit-hair/elit-tools";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { callSvcAxiosInstance } from "@/constants";
import { incidentEndpoints } from "@/constants/endpoints";
import { DEFAULT_EMPTY_PAGINATION } from "@/constants/pagination";
import type { IncidentAudioDevicesSnapshot } from "@/pages/incidents/utils/collect-incident-data";
import {
    IncidentAnalytics,
    IncidentAnalyticsFilters,
    IncidentReport,
    IncidentReportFilters,
} from "@/types/incident.types";
import type { AudioDeviceEvent } from "@/utils/audio-device-event-log.utils";

export type CreateIncidentPayload = {
    description: string;
    data: {
        "local-storage": Record<string, string>;
        "audio-devices"?: IncidentAudioDevicesSnapshot;
        "audio-device-log"?: AudioDeviceEvent[];
    };
};

export type UpdateIncidentPayload = {
    id: string;
    status?: IncidentReport["status"];
    type?: string;
};

export const incidentKeys = {
    all: ["incidents"] as const,
    lists: () => [...incidentKeys.all, "list"] as const,
    list: (filters: IncidentReportFilters) => [...incidentKeys.lists(), filters] as const,
    analytics: () => [...incidentKeys.all, "analytics"] as const,
    analyticsWithFilters: (filters: IncidentAnalyticsFilters) => [...incidentKeys.analytics(), filters] as const,
};

const getIncidentsRequest = (filters: IncidentReportFilters): Promise<PaginatedResponse<IncidentReport>> =>
    callSvcAxiosInstance
        .get<PaginatedResponse<IncidentReport>>(incidentEndpoints.getIncidents, { params: filters })
        .then((res) => res.data ?? { data: [], pagination: DEFAULT_EMPTY_PAGINATION });

export const useGetIncidents = (filters: IncidentReportFilters = {}, options?: { enabled?: boolean }) =>
    useQuery({
        queryKey: incidentKeys.list(filters),
        queryFn: () => getIncidentsRequest(filters),
        enabled: options?.enabled ?? true,
    });

const getIncidentAnalyticsRequest = (filters: IncidentAnalyticsFilters): Promise<IncidentAnalytics> =>
    callSvcAxiosInstance
        .get<IncidentAnalytics>(incidentEndpoints.getIncidentAnalytics, { params: filters })
        .then((res) => res.data);

export const useGetIncidentAnalytics = (filters: IncidentAnalyticsFilters = {}) =>
    useQuery({
        queryKey: incidentKeys.analyticsWithFilters(filters),
        queryFn: () => getIncidentAnalyticsRequest(filters),
    });

const createIncidentRequest = (payload: CreateIncidentPayload) =>
    callSvcAxiosInstance.post(incidentEndpoints.createIncident, payload);

export const useCreateIncident = () =>
    useMutation({
        mutationFn: createIncidentRequest,
    });

const updateIncidentRequest = ({ id, ...payload }: UpdateIncidentPayload): Promise<IncidentReport> =>
    callSvcAxiosInstance.patch<IncidentReport>(incidentEndpoints.updateIncident(id), payload).then((res) => res.data);

export const useUpdateIncident = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateIncidentRequest,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: incidentKeys.lists() });
        },
    });
};

type RemoveIncidentReviewerPayload = {
    id: string;
    reviewerId: string;
};

const removeIncidentReviewerRequest = ({ id, reviewerId }: RemoveIncidentReviewerPayload): Promise<IncidentReport> =>
    callSvcAxiosInstance
        .delete<IncidentReport>(incidentEndpoints.removeReviewer(id, reviewerId))
        .then((res) => res.data);

export const useRemoveIncidentReviewer = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: removeIncidentReviewerRequest,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: incidentKeys.lists() });
        },
    });
};
